import {
  KeyboardArrowDown,
  FileDownload,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { formatDate } from "../../utils";
import { useDispatch } from "react-redux";
import { getUnlistedHoldings } from "../../redux/rm/rmSlice";
import * as XLSX from "xlsx";

const TransactionReport = () => {
  const dispatch = useDispatch<any>();
  const userId = Number(localStorage.getItem("ID") || "0");
  const userType = Number(localStorage.getItem("userType") || "0");
  const [holdingsData, setHoldingsData] = React.useState<any>([]);
  const [transactionsData, setTransactionsData] = React.useState<any>([]);

  useEffect(() => {
    getUnlistedHoldingData();
  }, []);

  const getUnlistedHoldingData = async () => {
    try {
      const res = await dispatch(
        getUnlistedHoldings({ rmId: userType === 4 ? 0 : userId })
      );
      if (res?.error?.message) {
        return;
      }
      setHoldingsData(res?.payload?.data?.holdings);
      setTransactionsData(res?.payload?.data?.transactions);
    } catch (error) {
      console.log(error, "error");
    }
  };

  const exportToExcel = () => {
    // Prepare holdings summary data
    const holdingsSummaryData = holdingsData.map((holding: any) => {
      const transactions = transactionsData.filter(
        (t: any) => t.stockId === holding.stockId
      );

      const buyQty = transactions
        .filter((t: any) => t.type === "BUY")
        .reduce((sum: number, t: any) => sum + t.quantity, 0);
      const sellQty = transactions
        .filter((t: any) => t.type === "SELL")
        .reduce((sum: number, t: any) => sum + t.quantity, 0);

      return {
        "Stock Name": holding.stockName,
        "Avg. Buy Price": holding.avgBuyPrice?.toFixed(2) || 0,
        "Avg. Sell Price": holding.avgSellPrice?.toFixed(2) || 0,
        "Buy Quantity": buyQty,
        "Sell Quantity": sellQty,
        "Total Invested": holding.totalInvested?.toFixed(2) || 0,
        "Profit/Loss": holding.profitLoss?.toFixed(2) || 0,
      };
    });

    // Prepare detailed transactions data
    const transactionsDetailData = transactionsData.map((txn: any) => ({
      "Stock Name":
        holdingsData.find((h: any) => h.stockId === txn.stockId)?.stockName ||
        "-",
      "Investor Name": txn.investorName,
      ...(userType === 4 ? { "RM Name": txn.rmName || "-" } : {}),
      Type: txn.type,
      Date: formatDate(txn.createdAt),
      Quantity: txn.quantity,
      "Buy/Sell Price": txn.buySellPrice,
      "Brokerage": txn.brokerage,
      "B.Name": txn.brokerName,
      Amount: (txn.quantity * (txn.type === "Buy" ? txn.buySellPrice - txn.brokerage : txn.buySellPrice + txn.brokerage)).toFixed(2),
    }));

    // Create workbook with multiple sheets
    const workbook = XLSX.utils.book_new();

    // Add Holdings Summary sheet
    const holdingsWorksheet = XLSX.utils.json_to_sheet(holdingsSummaryData);
    XLSX.utils.book_append_sheet(
      workbook,
      holdingsWorksheet,
      "Holdings Summary"
    );

    // Add Transactions Detail sheet
    const transactionsWorksheet = XLSX.utils.json_to_sheet(
      transactionsDetailData
    );
    XLSX.utils.book_append_sheet(
      workbook,
      transactionsWorksheet,
      "Transaction Details"
    );

    // Generate filename with current date
    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `Transaction_Report_${currentDate}.xlsx`;

    // Save the file
    XLSX.writeFile(workbook, filename);
  };

  return (
    <div
      className="min-h-screen bg-lightBg py-8 px-4 sm:px-6 lg:px-8"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginBottom: "20px",
      }}
    >
      <Box
        width="90%"
        mb={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" style={{ textAlign: "left" }}>
          Transaction Report
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FileDownload />}
          onClick={exportToExcel}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
            py: 1,
          }}
        >
          Export Data
        </Button>
      </Box>
      <Box width="90%" mt={2}>
        <Box width="100%" mt={2}>
          {/* Summary Header */}
          <Box
            sx={{ display: "flex", p: 1, bgcolor: "grey.100", fontWeight: 600 }}
          >
            <Box sx={{ flex: 2 }}>Stock Name</Box>
            <Box sx={{ flex: 1.5 }}>Avg. Buy</Box>
            <Box sx={{ flex: 1.5 }}>Avg. Sell</Box>
            <Box sx={{ flex: 1.5 }}>Buy Qty.</Box>
            <Box sx={{ flex: 1.5 }}>Sell Qty.</Box>
            <Box sx={{ flex: 1.5 }}>Invested</Box>
            <Box sx={{ flex: 1 }}>P & L</Box>
            <Box sx={{ width: 40 }} />
          </Box>

          {holdingsData.map((holding: any) => {
            const transactions = transactionsData.filter(
              (t: any) => t.stockId === holding.stockId
            );

            const buyQty = transactions
              .filter((t: any) => t.type === "BUY")
              .reduce((sum: number, t: any) => sum + t.quantity, 0);
            const sellQty = transactions
              .filter((t: any) => t.type === "SELL")
              .reduce((sum: number, t: any) => sum + t.quantity, 0);

            const isProfit = holding.profitLoss >= 0;

            return (
              <Accordion key={holding.stockId}>
                <AccordionSummary
                  expandIcon={<KeyboardArrowDown sx={{ ml: 3 }} />}
                >
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ flex: 2 }}>{holding.stockName}</Box>
                    <Box sx={{ flex: 1.5 }}>
                      ₹{holding.avgBuyPrice?.toFixed(2) || 0}
                    </Box>
                    <Box sx={{ flex: 1.5 }}>
                      ₹{holding.avgSellPrice?.toFixed(2) || 0}
                    </Box>
                    <Box sx={{ flex: 1.5 }}>{buyQty}</Box>
                    <Box sx={{ flex: 1.5 }}>{sellQty}</Box>
                    <Box sx={{ flex: 1.5 }}>
                      ₹{holding.totalInvested?.toFixed(2) || 0}
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        color: isProfit ? "green" : "red",
                      }}
                    >
                      ₹{holding.profitLoss?.toFixed(2) || 0}
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Equity Transactions
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Investor</TableCell>
                        {userType === 4 && <TableCell>RM Name</TableCell>}
                        <TableCell>Type</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Buy/Sell Price</TableCell>
                        <TableCell>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((txn: any) => (
                        <TableRow key={txn.transactionId}>
                          <TableCell>{txn.investorName}</TableCell>
                          {userType === 4 && (
                            <TableCell>{txn.rmName || "-"}</TableCell>
                          )}
                          <TableCell>{txn.type}</TableCell>
                          <TableCell>{formatDate(txn.createdAt)}</TableCell>
                          <TableCell>{txn.quantity}</TableCell>
                          <TableCell>₹{txn.buySellPrice}</TableCell>
                          <TableCell>
                            ₹{(txn.quantity * txn.buySellPrice).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Box>
    </div>
  );
};

export default TransactionReport;
